import React, { useState, useEffect, useCallback } from 'react';
import ModalControl, { ModalBody } from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/modalControl';
import { apiFetchData, apiPostDataWithLangISO } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
import { reportError } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';
import userData from '@PROJECT_ROOT/assets/responsive-frontend/js/services/userData';
import { charToColor, backgroundColorToTextColor } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/profileAvatarUtils';
import { toast } from 'react-toastify';
import { MdCheckCircle } from 'react-icons/md';

export default function NewsletterOffer() {
    const newsletterOfferPathExtension = 'social-registration/';
    const postPathExtension = 'social-registration/subscribe-to/';
    const user = userData.getDecodedUserDataFromLocalStorage();
    const userId = user !== '0' ? user?.id : null;

    const [publicResources, setPublicResources] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPostLoading, setIsPostLoading] = useState(false);

    const getData = useCallback(() => {
        apiFetchData(newsletterOfferPathExtension)
            .then((res) => {
                setPublicResources(res.data);
                setIsModalOpen(true);
            })
            .catch((err) => {
                reportError(err);
            });
    }, []);

    useEffect(() => {
        if (!userId) return;
        if (sessionStorage.getItem(`newsletterOfferResolved:${userId}`)) return;
        if (!publicResources) {
            getData();
        }
    }, [publicResources, getData, userId]);

    const handleSubmitForm = (subscribed) => {
        const data = {
            subscribed,
        };
        setIsPostLoading(true);
        apiPostDataWithLangISO(postPathExtension, data)
            .then((res) => {
                if (res.status === 200) {
                    if (res.data.status === 'failure' && res.data.message) {
                        toast.info(res.data.message.title);
                    }
                    if (res.data.status === 'success' && res.data.message) {
                        toast.success(res.data.message.title);
                    }
                    sessionStorage.setItem(`newsletterOfferResolved:${userId}`, '1');
                    return;
                }
                if (res.status === 204) {
                    sessionStorage.setItem(`newsletterOfferResolved:${userId}`, '1');
                    return;
                }
                reportError(res);
            })
            .catch((err) => {
                reportError(err);
            })
            .finally(() => {
                setIsPostLoading(false);
                setIsModalOpen(false);
            });
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        handleSubmitForm(false);
    };

    const handleSubmitClick = () => {
        handleSubmitForm(true);
    };

    const firstChar = userId ? (user.firstname || user.username).charAt(0) : null;
    const backgroundColor = firstChar ? charToColor(firstChar) : null;
    const color = backgroundColor ? backgroundColorToTextColor(backgroundColor) : null;

    return publicResources && (
        <ModalControl
            id="newsletterOfferModal"
            title={publicResources?.mainTitle}
            isOpen={isModalOpen}
            disableBodyScroll={true}
            onClose={handleModalClose}
            onEsc={handleModalClose}
            animate={true}
            leftFooterButton={
                {
                    content: publicResources?.newsletterSubscriptionForm.cancelAction.title,
                    onClick: handleModalClose,
                }
            }
            rightFooterButton={
                {
                    content: publicResources?.newsletterSubscriptionForm.submitAction.title,
                    onClick: handleSubmitClick,
                }
            }
        >
            <ModalBody>
                <div className="newsletter-offer__container">
                    {userId && (
                        <div className="user-avatar-circle userCircleAvatar" style={{ backgroundColor, color }}>
                            {firstChar}
                        </div>
                    )}
                    <div className="newsletter-offer__title">
                        <h2 className="newsletter-offer__title-text">{publicResources.mainTitle}</h2>
                        <span className="newsletter-offer__subtitle-text">{publicResources.subtitle}</span>
                    </div>
                    <div className="newsletter-offer__content">
                        <div className="content-title">
                            <h3 className="content-title__text">{publicResources.newsletterTitle}</h3>
                            <span className="content-subtitle__text">{publicResources.newsletterSubtitle}</span>
                        </div>
                        <ul className="content-benefits__list">
                            {publicResources.newsletterBenefits.map((item) => (
                                <li className="content-benefits__list-item" key={item}>
                                    <MdCheckCircle className="content-benefits__list-item-icon" />
                                    <span className="content-benefits__list-item-text">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <form className={`newsletter-offer__form form__wrapper d-flex ${isPostLoading ? 'disableCurrentElementContent--spinner' : ''}`}>
                        <p
                            className="newsletter-offer__form-info"
                            dangerouslySetInnerHTML={{ __html: publicResources.termsAndConditionsText }}
                        />
                        <p className="newsletter-offer__form-info">{publicResources.consentWithdrawalText}</p>
                    </form>
                </div>
            </ModalBody>
        </ModalControl>
    );
}
